import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { useMediaQuery, useTheme } from "@mui/material";
import ModalBasicLayout from "../ModalBasicLayout";
import Typography from "@mui/material/Typography";
import Button from "../../Buttons/Button";
import Loader from "../../Loaders/Loader";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { useContentModals } from "../../../contexts/contentModalsContext";
import {
  applyVideoEditorEdl,
  resetVideoEditorProject,
  setVideoEditorPlayhead,
  startAddMediaClipToVideoEditor,
  startExportVideoEditorProject,
  startInitVideoEditorFromSource,
} from "../../../actions/videoEditor";
import { startAddVideoEditorSubtitles } from "../../../actions/videoSubtitles";
import VideoEditorPlayer from "../../../features/videoEditor/VideoEditorPlayer";
import VideoEditorTimeline from "../../../features/videoEditor/VideoEditorTimeline";
import VideoEditorPropertiesDrawer from "../../../features/videoEditor/VideoEditorPropertiesDrawer";
import { VideoEditorPlaybackClockProvider } from "../../../features/videoEditor/videoEditorPlaybackClock";
import useVideoEditorHistory from "../../../features/videoEditor/useVideoEditorHistory";
import {
  selectVideoEditorEdl,
  selectVideoEditorIsExporting,
} from "../../../features/videoEditor/videoEditorSelectors";
import {
  VIDEO_EDITOR_PREVIEW_BUSY_Z_INDEX,
  VIDEO_EDITOR_TRACK_TYPE,
} from "../../../features/videoEditor/constants";
import {
  isVideoEditorSpaceToggleEvent,
  isVideoEditorTypingTarget,
} from "../../../features/videoEditor/videoEditorKeyboard";
import ModalSubtitleOptions from "../ModalCreateWithIA/ModalSubtitleOptions";
import { DEFAULT_SUBTITLE_OPTIONS } from "../ModalCreateWithIA/subtitlePresets";
import {
  ALERT_ICON_TYPE_ERROR,
  AUDIO_FORMATS,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
} from "../../../utils/constants";
import {
  BUTTON_SAVE,
  ERROR,
  FIELD_FILE_VALID_REQUIRED,
  MODAL_IMAGE_EDIT_REPLACE_BUTTON,
  MODAL_IMAGE_EDIT_SAVE_AS_NEW_BUTTON,
  MODAL_VIDEO_EDITOR_ARIA_LABEL,
  MODAL_VIDEO_EDITOR_ANALYZE_SUBTITLES_ERROR,
  MODAL_VIDEO_EDITOR_ANALYZING_SUBTITLES,
  MODAL_VIDEO_EDITOR_EXPORTING,
  MODAL_VIDEO_EDITOR_SAVE_ALERT_DESCRIPTION,
  MODAL_VIDEO_EDITOR_SAVE_ALERT_TITLE,
  MODAL_VIDEO_EDITOR_SAVE_ERROR,
  MODAL_VIDEO_EDITOR_TITLE,
} from "../../../i18n/keysTranslations";
import { SimpleAlert, ConfirmAlert } from "../../Alerts/Alerts";
import { getExtensionFile, isLikelyImageUrl } from "../../../utils/string";
import { startUploadFileAws } from "../../../services/public";

const VIDEO_EDITOR_AUDIO_PICKER_FORMATS = [
  "mp3",
  "wav",
  "m4a",
  "aac",
  "ogg",
  "mpeg",
];

const VIDEO_EDITOR_AUDIO_INPUT_ACCEPT =
  "audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/aac,audio/ogg,.mp3,.wav,.m4a,.aac,.ogg";

const getAudioDurationFromFile = ({ file }) =>
  new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const audio = document.createElement("audio");
    audio.preload = "metadata";

    const cleanup = () => {
      audio.onloadedmetadata = null;
      audio.onerror = null;
      URL.revokeObjectURL(objectUrl);
    };

    audio.onloadedmetadata = () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : null;
      cleanup();
      resolve(duration);
    };

    audio.onerror = () => {
      cleanup();
      resolve(null);
    };

    audio.src = objectUrl;
  });

const isAllowedAudioFile = ({ file }) => {
  if (file.type.startsWith("audio/")) {
    return true;
  }

  const extension = (file.name.split(".").pop() || "").toLowerCase();
  return (
    VIDEO_EDITOR_AUDIO_PICKER_FORMATS.includes(extension) ||
    AUDIO_FORMATS.includes(extension)
  );
};

const getClipTypeFromGalleryItem = ({ item }) => {
  const url = item?.url || "";
  const extension = getExtensionFile(url);
  const itemType = item?.type;

  if (
    VIDEO_EDITOR_AUDIO_PICKER_FORMATS.includes(extension) ||
    AUDIO_FORMATS.includes(extension)
  ) {
    return VIDEO_EDITOR_TRACK_TYPE.AUDIO;
  }

  if (
    itemType === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
    VIDEO_FORMATS.includes(extension)
  ) {
    return VIDEO_EDITOR_TRACK_TYPE.VIDEO;
  }

  if (
    itemType === VIDEO_EDITOR_TRACK_TYPE.IMAGE ||
    IMAGE_FORMATS.includes(extension) ||
    isLikelyImageUrl(url)
  ) {
    return VIDEO_EDITOR_TRACK_TYPE.IMAGE;
  }

  return VIDEO_EDITOR_TRACK_TYPE.VIDEO;
};

const ModalVideoEditor = ({
  modalOpen = false,
  onCloseModal = () => {},
  onCallbackSave = () => {},
  sourceUrl = "",
  subtitles = null,
  sourceAssetID = "",
  sourceName = "",
  pathStorage = "",
  persistToContentList = true,
  compositionWidth,
  compositionHeight,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { openGallery, close } = useContentModals();
  const edl = useSelectorApp(selectVideoEditorEdl);
  const isExporting = useSelectorApp(selectVideoEditorIsExporting);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isAnalyzingSubtitles, setIsAnalyzingSubtitles] = useState(false);
  const [isSubtitleOptionsOpen, setIsSubtitleOptionsOpen] = useState(false);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [propertiesOpen, setPropertiesOpen] = useState(!isSmallScreen);
  const playerApiRef = useRef(null);
  const audioInputRef = useRef(null);
  const nestedModalIdsRef = useRef([]);
  const isGalleryOpenRef = useRef(false);

  useVideoEditorHistory({ enabled: modalOpen && Boolean(edl) });

  useEffect(() => {
    const nestedModalIds = nestedModalIdsRef;
    return () => {
      nestedModalIds.current.forEach((id) => close(id));
      nestedModalIds.current = [];
      isGalleryOpenRef.current = false;
    };
  }, [close]);

  useEffect(() => {
    if (!modalOpen || !sourceUrl) return undefined;

    let isMounted = true;
    const onInit = async () => {
      setIsInitializing(true);
      await dispatch(
        startInitVideoEditorFromSource({
          sourceUrl,
          subtitles,
          sourceAssetID,
          sourceName,
          compositionWidth,
          compositionHeight,
        })
      );
      if (!isMounted) {
        return;
      }
      setIsInitializing(false);
    };

    onInit();

    return () => {
      isMounted = false;
      dispatch(resetVideoEditorProject());
    };
  }, [
    compositionHeight,
    compositionWidth,
    dispatch,
    modalOpen,
    sourceAssetID,
    sourceName,
    sourceUrl,
    subtitles,
  ]);

  const onPlayheadChange = useCallback(
    ({ playhead }) => {
      dispatch(setVideoEditorPlayhead({ playhead }));
    },
    [dispatch]
  );

  const onPlayingChange = useCallback(({ isPlaying: nextIsPlaying }) => {
    setIsPlaying(nextIsPlaying);
  }, []);

  useEffect(() => {
    if (!modalOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (!isVideoEditorSpaceToggleEvent(event)) {
        return;
      }
      if (isSubtitleOptionsOpen || isGalleryOpenRef.current) {
        return;
      }
      const target = event.target;
      if (isVideoEditorTypingTarget(target)) {
        return;
      }
      if (target?.closest?.('[role="menu"], [role="listbox"]')) {
        return;
      }

      event.preventDefault();
      playerApiRef.current?.togglePlaying?.();
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isSubtitleOptionsOpen, modalOpen]);

  const onAddGalleryItems = useCallback(
    async ({ items }) => {
      const item = Array.isArray(items) ? items[0] : items;
      if (!item?.url) return;

      const clipType = getClipTypeFromGalleryItem({ item });

      await dispatch(
        startAddMediaClipToVideoEditor({
          sourceUrl: item.url,
          type: clipType,
          label: item.name || clipType,
          duration: item.duration || item.mediaData?.duration,
        })
      );
    },
    [dispatch]
  );

  const onAddToTrack = useCallback(
    async ({ type }) => {
      if (type === VIDEO_EDITOR_TRACK_TYPE.SUBTITLE) {
        if (isAnalyzingSubtitles) return;
        setIsAnalyzingSubtitles(true);
        try {
          const result = await dispatch(
            startAddVideoEditorSubtitles({
              sourceUrl,
            })
          );
          if (!result?.ok) {
            SimpleAlert({
              icon: ALERT_ICON_TYPE_ERROR,
              title: t(ERROR),
              text: t(MODAL_VIDEO_EDITOR_ANALYZE_SUBTITLES_ERROR),
            });
          }
        } finally {
          setIsAnalyzingSubtitles(false);
        }
        return;
      }

      if (type === VIDEO_EDITOR_TRACK_TYPE.AUDIO) {
        audioInputRef.current?.click();
        return;
      }

      if (isGalleryOpenRef.current) {
        return;
      }

      isGalleryOpenRef.current = true;
      const galleryId = openGallery({
        multiple: false,
        onCallbackSelectedItems: (items) => onAddGalleryItems({ items }),
        acceptedFormats: IMAGE_FORMATS.concat(VIDEO_FORMATS),
        pathStorage,
        showFooterUseSelectedButton: true,
        dialogZIndex: 1500,
        showAdAccountOption: false,
        showGoogleDriveOption: false,
        showSocialPostsOption: false,
        enableHistoryManagement: true,
        showUploadDeviceOption: true,
        onCloseModal: () => {
          isGalleryOpenRef.current = false;
          nestedModalIdsRef.current = nestedModalIdsRef.current.filter(
            (id) => id !== galleryId
          );
        },
      });
      nestedModalIdsRef.current.push(galleryId);
    },
    [dispatch, isAnalyzingSubtitles, onAddGalleryItems, openGallery, pathStorage, sourceUrl, t]
  );

  const onAudioFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      if (!pathStorage) {
        SimpleAlert({
          icon: ALERT_ICON_TYPE_ERROR,
          title: t(ERROR),
          text: t(MODAL_VIDEO_EDITOR_SAVE_ERROR),
        });
        return;
      }

      if (!isAllowedAudioFile({ file })) {
        SimpleAlert({
          icon: ALERT_ICON_TYPE_ERROR,
          title: t(ERROR),
          text: t(FIELD_FILE_VALID_REQUIRED, {
            value: VIDEO_EDITOR_AUDIO_PICKER_FORMATS.join(", "),
          }),
        });
        return;
      }

      setIsUploadingAudio(true);
      try {
        const mimeType = file.type || "audio/mpeg";
        const uploadedUrl = await dispatch(
          startUploadFileAws(file, mimeType, pathStorage)
        );

        if (!uploadedUrl) {
          return;
        }

        const duration = await getAudioDurationFromFile({ file });
        await dispatch(
          startAddMediaClipToVideoEditor({
            sourceUrl: uploadedUrl,
            type: VIDEO_EDITOR_TRACK_TYPE.AUDIO,
            label: file.name.replace(/\.[^.]+$/, ""),
            duration,
          })
        );
      } finally {
        setIsUploadingAudio(false);
      }
    },
    [dispatch, pathStorage, t]
  );

  const hasClips = Boolean(Object.keys(edl?.clips?.byId || {}).length);

  const onSave = useCallback(async () => {
    const exportProject = async (mode) => {
      const result = await dispatch(
        startExportVideoEditorProject({
          pathStorage,
          sourceName: sourceName || "video",
          mode,
          sourceAssetID,
          persistToContentList,
        })
      );

      if (!result?.ok) {
        SimpleAlert({
          icon: "error",
          title: t(MODAL_VIDEO_EDITOR_SAVE_ERROR),
        });
        return;
      }

      await onCallbackSave(result);
      onCloseModal();
    };

    if (!sourceAssetID && persistToContentList) {
      await exportProject("new");
      return;
    }

    const response = await ConfirmAlert({
      title: t(MODAL_VIDEO_EDITOR_SAVE_ALERT_TITLE),
      text: t(MODAL_VIDEO_EDITOR_SAVE_ALERT_DESCRIPTION),
      showCancelButton: true,
      showThirdButton: true,
      confirmButtonText: t(MODAL_IMAGE_EDIT_REPLACE_BUTTON),
      thirdButtonText: t(MODAL_IMAGE_EDIT_SAVE_AS_NEW_BUTTON),
      confirmButtonVariant: "secondary",
      cancelButtonVariant: "secondary",
      denyButtonVariant: "secondary",
      width: "550px",
    });

    if (response?.isConfirmed) {
      await exportProject("replace");
    } else if (response?.isDenied) {
      await exportProject("new");
    }
  }, [
    dispatch,
    onCallbackSave,
    onCloseModal,
    pathStorage,
    persistToContentList,
    sourceAssetID,
    sourceName,
    t,
  ]);

  return (
    <VideoEditorPlaybackClockProvider>
      <input
        ref={audioInputRef}
        type="file"
        hidden
        accept={VIDEO_EDITOR_AUDIO_INPUT_ACCEPT}
        onChange={onAudioFileChange}
      />
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        fullWidth
        fullScreen
        scroll="paper"
        title={t(MODAL_VIDEO_EDITOR_TITLE)}
        titleContent={false}
        ariaLabel={t(MODAL_VIDEO_EDITOR_ARIA_LABEL)}
        sxTitle={{
          py: 1,
          px: 5,
          "& > .MuiTypography-root": {
            mr: "auto",
          },
        }}
        customComponentTitle={
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: "auto" }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={onSave}
              disabled={isInitializing || isExporting || !edl || !hasClips}
              loading={isExporting}
            >
              {isExporting ? t(MODAL_VIDEO_EDITOR_EXPORTING) : t(BUTTON_SAVE)}
            </Button>
          </Box>
        }
        sxContent={{
          p: 0,
          pl: 3,
          pb: 3,
          mt: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "row",
            position: "relative",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0 }}>
            {isInitializing || !edl ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Loader />
              </Box>
            ) : (
              <Box sx={{ position: "relative", height: "100%" }}>
                <VideoEditorPlayer
                  ref={playerApiRef}
                  onPlayheadChange={onPlayheadChange}
                  onPlayingChange={onPlayingChange}
                  isPlaying={isPlaying}
                  onOpenSubtitleOptions={() => setIsSubtitleOptionsOpen(true)}
                  showPropertiesToggle={!propertiesOpen}
                  onOpenProperties={() => setPropertiesOpen(true)}
                />
                {isUploadingAudio ? (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(255,255,255,0.7)",
                      zIndex: VIDEO_EDITOR_PREVIEW_BUSY_Z_INDEX,
                    }}
                  >
                    <Loader />
                  </Box>
                ) : null}
                {isAnalyzingSubtitles ? (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 16,
                      right: 16,
                      bottom: 16,
                      zIndex: VIDEO_EDITOR_PREVIEW_BUSY_Z_INDEX,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      bgcolor: "rgba(255,255,255,0.92)",
                      boxShadow: "0 4px 16px rgba(15, 23, 42, 0.12)",
                      pointerEvents: "none",
                    }}
                  >
                    <Loader size={22} hasMarginTop={false} />
                    <Typography sx={{ fontSize: 13 }}>
                      {t(MODAL_VIDEO_EDITOR_ANALYZING_SUBTITLES)}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            )}
          </Box>
          {!isInitializing && edl ? (
            <VideoEditorTimeline onAddToTrack={onAddToTrack} />
          ) : null}
          </Box>
          {!isInitializing && edl ? (
            <VideoEditorPropertiesDrawer
              open={propertiesOpen}
              onOpenChange={setPropertiesOpen}
            />
          ) : null}
        </Box>
      </ModalBasicLayout>
      <ModalSubtitleOptions
        modalOpen={isSubtitleOptionsOpen}
        onCloseModal={() => setIsSubtitleOptionsOpen(false)}
        persistAdvertiserConfiguration={false}
        initialValues={edl?.subtitleOptions || DEFAULT_SUBTITLE_OPTIONS}
        onSave={(options) => {
          if (!edl) {
            return;
          }
          dispatch(
            applyVideoEditorEdl({
              edl: {
                ...edl,
                subtitleOptions: {
                  ...edl.subtitleOptions,
                  ...options,
                  ...(edl.subtitleOptions?.position
                    ? { position: edl.subtitleOptions.position }
                    : {}),
                  ...(edl.subtitleOptions?.enabled === false
                    ? { enabled: false }
                    : {}),
                },
              },
            })
          );
        }}
      />
    </VideoEditorPlaybackClockProvider>
  );
};

export default ModalVideoEditor;
